/**
 * Serialized initial state of a prerendered page.
 *
 * The build renders each public route with React and writes this state into the HTML
 * (<script id="bricolat-state" type="application/json">), so the client's first render starts from the
 * same data as the HTML and no content swaps in after load.
 * NB: the element id differs from the global name on purpose: an element id becomes `window[id]`.
 */
export interface PrerenderState {
  /** Pathname the state was produced for; a state is never applied to another route. */
  route: string;
  /** Public API responses (unwrapped payloads) read by this route's first render, by canonical request key. */
  api?: Record<string, unknown>;
  /** Provider manifest hash and the ids this page asked about (see providerManifest.ts). */
  manifest?: { hash: string; ids: string[]; partial?: boolean };
}

type StateGlobal = typeof globalThis & { __BRICOLAT_STATE__?: PrerenderState };

const g = globalThis as StateGlobal;
const consumed = new Set<string>();
const norm = (p: string) => p.replace(/(.)\/$/, '$1');

/** Client only: read the state script written by the build. */
export const loadInlineState = (): void => {
  if (typeof document === 'undefined' || g.__BRICOLAT_STATE__) return;
  const el = document.getElementById('bricolat-state');
  if (!el?.textContent) return;
  try {
    g.__BRICOLAT_STATE__ = JSON.parse(el.textContent) as PrerenderState;
  } catch {
    /* unreadable state: the page then behaves like a plain SPA load */
  }
};

/**
 * State for the current route until the page that uses it has mounted. It cannot be tied to "first render of
 * the app": a lazy page inside a dehydrated Suspense boundary hydrates after the layout's first commit.
 */
export const getPrerenderState = (pathname?: string): PrerenderState | undefined => {
  const state = g.__BRICOLAT_STATE__;
  if (!state) return undefined;
  const path = norm(pathname ?? (typeof window !== 'undefined' ? window.location.pathname : state.route));
  return norm(state.route) === path && !consumed.has(path) ? state : undefined;
};

/** Manifest serialized in the page; unlike `api` it stays readable after the route's state is consumed. */
export const getInlineManifest = (): PrerenderState['manifest'] => g.__BRICOLAT_STATE__?.manifest;

/** Called by a page once mounted: later visits to the same route start from a normal client render. */
export const consumePrerenderState = (pathname: string): void => {
  if (typeof window !== 'undefined') consumed.add(norm(pathname));
};

/** Server side (build): state for the page being rendered. */
export const setServerState = (state: PrerenderState | undefined): void => {
  g.__BRICOLAT_STATE__ = state;
};
